import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Divider, Collapse, Drawer, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import * as MuiIcons from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { superAdminMenu, superAdminFooterNav } from '../../config/superAdminMenu';
import { brandColors, TYPE, brandCss } from '../../theme/appTheme';
import { useModules } from '../../contexts/ModuleContext';
import { sortSidebarParents, SIDEBAR_GROUP_ORDER, SIDEBAR_GROUP_LABELS } from '../../config/sidebarMenu';
import { TenantBrandLogo } from '../ui/TenantBrandLogo';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { APP_NAME } from '../../constants/brand';

const DRAWER_WIDTH = 268;
const COLLAPSED_WIDTH = 76;
const SUPER_ADMIN_LOGO_HEIGHT = 60;
const SUPER_ADMIN_LOGO_COLLAPSED = 48;
const MENU_DIVIDER = '1px solid rgba(255,255,255,0.10)';

const getIcon = (name: string, sx?: object) => {
  const Icon = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name] || MuiIcons.Folder;
  return <Icon sx={sx} />;
};

function isNavItemActive(path: string, pathname: string): boolean {
  if (path === '/super-admin') return pathname === '/super-admin';
  if (path === '/dashboard') return pathname === '/dashboard';
  if (path.startsWith('/super-admin/master/')) {
    return pathname === path || pathname.startsWith(path);
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

type SidebarNavItem = { key?: string; label: string; path: string; icon: string; parentKey?: string };

function isParentNavActive(item: SidebarNavItem, pathname: string, groupItems: SidebarNavItem[]): boolean {
  if (isNavItemActive(item.path, pathname)) return true;
  return groupItems
    .filter((child) => child.parentKey === item.key)
    .some((child) => isNavItemActive(child.path, pathname));
}

interface NavItemProps {
  item: { label: string; path: string; icon: string; description?: string; key?: string };
  collapsed: boolean;
  active: boolean;
  onNavigate: (path: string) => void;
  nested?: boolean;
  enhanced?: boolean;
}

const adminNavItemSx = (active: boolean, collapsed: boolean, nested: boolean | undefined, primary: string) => ({
  mx: 1,
  mb: 0.25,
  px: collapsed ? 1.5 : nested ? 2.5 : 2,
  pl: collapsed ? 1.5 : nested ? 3.5 : 2,
  py: '10px',
  borderRadius: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  cursor: 'pointer',
  justifyContent: collapsed ? 'center' : 'flex-start',
  position: 'relative' as const,
  bgcolor: active ? `${primary}24` : 'transparent',
  color: active ? '#fff' : nested ? 'rgba(255,255,255,0.58)' : 'rgba(255,255,255,0.68)',
  '&:hover': {
    bgcolor: active ? `${primary}33` : 'rgba(255,255,255,0.07)',
    color: '#fff',
  },
  transition: 'all 0.15s ease',
  ...(active && !collapsed && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: nested ? 12 : 0,
      top: '20%',
      bottom: '20%',
      width: 3,
      borderRadius: 2,
      bgcolor: primary,
    },
  }),
});

const NavItem: React.FC<NavItemProps> = ({ item, collapsed, active, onNavigate, nested, enhanced }) => {
  const { brand } = useAppTheme();
  if (!enhanced) {
    return (
      <Tooltip title={collapsed ? item.label : item.description || ''} placement="right">
        <Box onClick={() => onNavigate(item.path)} sx={adminNavItemSx(active, collapsed, nested, brand.primary)}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            {getIcon(item.icon, { fontSize: 20, color: active ? brandCss.primary : 'inherit' })}
          </Box>
          {!collapsed && (
            <Typography
              variant="body2"
              fontWeight={active ? 600 : TYPE.nav.fontWeight}
              sx={{
                fontSize: nested ? TYPE.helper.fontSize : TYPE.nav.fontSize,
                lineHeight: TYPE.nav.lineHeight,
                fontFamily: 'inherit',
              }}
            >
              {item.label}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={collapsed ? item.label : item.description || ''} placement="right">
      <Box
        onClick={() => onNavigate(item.path)}
        sx={{
          mx: 0,
          px: collapsed ? 1.5 : nested ? 2.5 : 2,
          py: '10px',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: 'pointer',
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: active ? '#fff' : nested ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.82)',
          bgcolor: nested && active ? `rgba(${brand.primaryRgb},0.28)` : 'transparent',
          '&:hover': {
            bgcolor: active ? `rgba(${brand.primaryRgb},0.38)` : 'rgba(255,255,255,0.08)',
            color: '#fff',
          },
          '& > .MuiSvgIcon-root, & > span, & > div:first-of-type': {
            width: 22,
            display: 'inline-flex',
            justifyContent: 'center',
            flexShrink: 0,
          },
        }}
      >
        {getIcon(item.icon, { fontSize: nested ? 18 : 20, color: active ? brand.primary : 'inherit' })}
        {!collapsed && (
          <Typography
            variant="body2"
            fontWeight={active ? 600 : TYPE.nav.fontWeight}
            sx={{
              fontSize: TYPE.nav.fontSize,
              lineHeight: TYPE.nav.lineHeight,
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

interface NavAccordionProps {
  item: SidebarNavItem;
  subItems: SidebarNavItem[];
  collapsed: boolean;
  pathname: string;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
  enhanced?: boolean;
}

const NavAccordion: React.FC<NavAccordionProps> = ({
  item, subItems, collapsed, pathname, expanded, onToggle, onNavigate, enhanced,
}) => {
  const { brand } = useAppTheme();
  const parentActive = isParentNavActive(item, pathname, [item, ...subItems]);
  const isOpen = expanded;

  if (collapsed) {
    return (
      <Box sx={{ borderBottom: MENU_DIVIDER }}>
        <NavItem
          item={item}
          collapsed={collapsed}
          active={parentActive}
          onNavigate={(path) => { onNavigate(path); if (!expanded) onToggle(); }}
          enhanced={enhanced}
        />
      </Box>
    );
  }

  const sectionLabel = item.key === 'vehicles'
    ? 'Vehicle list'
    : item.key === 'bilty'
      ? 'Bilty options'
      : item.key === 'gps_tracking'
        ? 'GPS options'
        : null;

  return (
    <Box sx={{
      borderBottom: isOpen ? `2px solid ${brand.primary}` : MENU_DIVIDER,
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          bgcolor: parentActive || isOpen ? `rgba(${brand.primaryRgb},0.18)` : 'transparent',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <NavItem
            item={item}
            collapsed={collapsed}
            active={parentActive}
            onNavigate={(path) => {
              onNavigate(path);
              if (!isOpen) onToggle();
            }}
            enhanced={enhanced}
          />
        </Box>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          sx={{
            color: '#fff',
            mr: 0.5,
            borderRadius: 0,
            width: 32,
            height: 32,
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
          }}
          aria-label={isOpen ? 'Collapse submenu' : 'Expand submenu'}
        >
          {isOpen ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>
      <Collapse in={isOpen} timeout={220} unmountOnExit>
        <Box sx={{
          bgcolor: `rgba(${brand.primaryRgb},0.10)`,
          borderLeft: `3px solid ${brand.primary}`,
        }}>
          {sectionLabel && (
            <Typography sx={{
              px: 2.5, pt: 0.75, pb: 0.35, display: 'block',
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 600, fontSize: TYPE.nav.fontSize, lineHeight: TYPE.nav.lineHeight, letterSpacing: 0.2, textTransform: 'none',
            }}>
              {sectionLabel}
            </Typography>
          )}
          {subItems.map((child) => (
            <NavItem
              key={child.path}
              item={child}
              collapsed={collapsed}
              active={isNavItemActive(child.path, pathname)}
              onNavigate={onNavigate}
              nested
              enhanced={enhanced}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export const Sidebar: React.FC = () => {
  const { grouped, collapsed, setCollapsed, mobileOpen, setMobileOpen } = useModules();
  const { user } = useAuth();
  const { brand } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showCollapsed = isMobile ? false : collapsed;
  const width = showCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const isSuperAdmin = user?.roleType === 'super_admin';
  const navGroups = isSuperAdmin ? null : grouped;

  const goTo = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile, setMobileOpen]);

  useEffect(() => {
    if (!navGroups) return;
    const autoExpanded: Record<string, boolean> = {};
    Object.values(navGroups).forEach((items) => {
      (items as SidebarNavItem[]).forEach((item) => {
        if (item.parentKey) return;
        const children = (items as SidebarNavItem[]).filter((c) => c.parentKey === item.key);
        if (children.length && isParentNavActive(item, location.pathname, items as SidebarNavItem[])) {
          autoExpanded[item.key || item.path] = true;
        }
      });
    });
    setExpandedMenus((prev) => ({ ...prev, ...autoExpanded }));
  }, [location.pathname, navGroups]);

  const toggleMenu = (key: string) => {
    setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const panelSx = {
    width,
    flexShrink: 0,
    bgcolor: isSuperAdmin ? brandColors.sidebarSuperAdminSolid : brand.sidebar,
    background: isSuperAdmin
      ? brandColors.sidebarSuperAdmin
      : `linear-gradient(180deg, ${brand.sidebar} 0%, ${brand.sidebarDark} 100%)`,
    color: !isSuperAdmin && brand.sidebarIsLight ? '#1a1033' : '#fff',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: isSuperAdmin ? '4px 0 24px rgba(4,21,40,0.45)' : `8px 0 40px rgba(${brand.primaryRgb},0.22)`,
    ...(isMobile ? {} : {
      position: 'fixed' as const,
      left: 0,
      top: 0,
      zIndex: 1200,
      transition: 'width 0.25s ease',
    }),
    '&::after': !isSuperAdmin ? {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 180,
      pointerEvents: 'none',
      background: `radial-gradient(ellipse at top, rgba(${brand.primaryRgb},0.28) 0%, transparent 70%)`,
    } : undefined,
  };

  const panel = (
    <Box sx={panelSx}>
      <Box sx={{
        p: isSuperAdmin ? 2 : showCollapsed ? 1 : 1.25,
        display: 'flex',
        alignItems: isSuperAdmin && !showCollapsed ? 'stretch' : 'center',
        flexDirection: isSuperAdmin && !showCollapsed ? 'column' : 'row',
        justifyContent: showCollapsed ? 'center' : 'space-between',
        position: 'relative',
        zIndex: 1,
        minHeight: isSuperAdmin ? (showCollapsed ? 80 : 120) : showCollapsed ? 56 : 72,
        borderBottom: MENU_DIVIDER,
      }}>
        {!showCollapsed ? (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            width: isSuperAdmin ? '100%' : undefined,
            pr: isSuperAdmin ? 4 : 0.5,
          }}>
            <TenantBrandLogo
              collapsed={false}
              height={isSuperAdmin ? SUPER_ADMIN_LOGO_HEIGHT : 52}
              isSuperAdmin={isSuperAdmin}
            />
          </Box>
        ) : (
          <Tooltip title={isSuperAdmin ? 'Super Admin' : (user?.company?.companyName || APP_NAME)} placement="right">
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <TenantBrandLogo
                collapsed
                height={isSuperAdmin ? SUPER_ADMIN_LOGO_COLLAPSED : 40}
                isSuperAdmin={isSuperAdmin}
              />
            </Box>
          </Tooltip>
        )}
        {!showCollapsed && !isMobile && (
          <IconButton
            size="small"
            onClick={() => setCollapsed(true)}
            sx={{
              color: 'rgba(255,255,255,0.65)',
              flexShrink: 0,
              borderRadius: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' },
              ...(isSuperAdmin && {
                position: 'absolute',
                top: 8,
                right: 8,
              }),
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        {showCollapsed && !isMobile && (
          <IconButton size="small" onClick={() => setCollapsed(false)}
            sx={{
              color: 'rgba(255,255,255,0.65)', position: 'absolute', right: -14, top: 28,
              bgcolor: isSuperAdmin ? brandColors.sidebarSuperAdminSolid : brand.sidebar,
              background: isSuperAdmin ? brandColors.sidebarSuperAdmin : undefined,
              border: '1px solid rgba(255,255,255,0.2)',
              zIndex: 2,
            }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{
        flex: 1, overflowY: 'auto', py: '10px', position: 'relative', zIndex: 1,
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 4 },
      }}>
        {isSuperAdmin ? (
          <>
            {superAdminMenu.map((group) => (
              <Box key={group.title} sx={{ mb: 0.5 }}>
                {!showCollapsed && (
                  <Typography variant="caption" sx={{
                    px: 2.5, py: 0.75, display: 'block', color: 'rgba(255,255,255,0.38)',
                    fontWeight: 600, letterSpacing: '0.06em', fontSize: 10, textTransform: 'uppercase',
                  }}>
                    {group.title}
                  </Typography>
                )}
                {group.items.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    collapsed={showCollapsed}
                    active={isNavItemActive(item.path, location.pathname)}
                    onNavigate={goTo}
                  />
                ))}
                {group.dividerAfter && !showCollapsed && (
                  <Divider sx={{ my: 1.25, mx: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
                )}
                {group.dividerAfter && showCollapsed && (
                  <Box sx={{ my: 1, mx: 1.5, height: 1, bgcolor: 'rgba(255,255,255,0.08)' }} />
                )}
              </Box>
            ))}
          </>
        ) : (
          <>
            {Object.keys(navGroups || {}).length === 0 && (
              <Typography sx={{ px: 2, py: 2, color: 'rgba(255,255,255,0.5)', fontSize: TYPE.secondary.fontSize, lineHeight: TYPE.secondary.lineHeight }}>
                Loading menu...
              </Typography>
            )}
            {SIDEBAR_GROUP_ORDER.map((groupKey) => {
              const groupItems = (navGroups?.[groupKey] || []) as SidebarNavItem[];
              if (!groupItems.length) return null;

              const parents = sortSidebarParents(groupItems.filter((i) => !i.parentKey));
              const sectionTitle = SIDEBAR_GROUP_LABELS[groupKey] || '';

              return (
                <Box key={groupKey} sx={{ mb: sectionTitle ? 0.75 : 0.25 }}>
                  {sectionTitle && !showCollapsed && (
                    <Typography
                      sx={{
                        px: 2,
                        pt: groupKey === 'People & Fleet' ? 0.5 : 1.25,
                        pb: 0.5,
                        color: 'rgba(255,255,255,0.42)',
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {sectionTitle}
                    </Typography>
                  )}
                  {sectionTitle && showCollapsed && (
                    <Box sx={{ my: 0.75, mx: 1.5, height: 1, bgcolor: 'rgba(255,255,255,0.12)' }} />
                  )}
                  {parents.map((item) => {
                    const children = sortSidebarParents(
                      groupItems.filter((child) => child.parentKey === item.key),
                    );
                    const menuKey = item.key || item.path;

                    if (children.length > 0) {
                      return (
                        <NavAccordion
                          key={item.path}
                          item={item}
                          subItems={children}
                          collapsed={showCollapsed}
                          pathname={location.pathname}
                          expanded={!!expandedMenus[menuKey]}
                          onToggle={() => toggleMenu(menuKey)}
                          onNavigate={goTo}
                          enhanced
                        />
                      );
                    }

                    return (
                      <Box key={item.path} sx={{ borderBottom: MENU_DIVIDER }}>
                        <NavItem
                          item={item}
                          collapsed={showCollapsed}
                          active={isNavItemActive(item.path, location.pathname)}
                          onNavigate={goTo}
                          enhanced
                        />
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
            {/* Any custom groups not in SIDEBAR_GROUP_ORDER */}
            {Object.keys(navGroups || {})
              .filter((k) => !(SIDEBAR_GROUP_ORDER as readonly string[]).includes(k))
              .map((groupKey) => {
                const groupItems = (navGroups?.[groupKey] || []) as SidebarNavItem[];
                if (!groupItems.length) return null;
                const parents = sortSidebarParents(groupItems.filter((i) => !i.parentKey));
                return (
                  <Box key={groupKey} sx={{ mb: 0.75 }}>
                    {!showCollapsed && (
                      <Typography
                        sx={{
                          px: 2, pt: 1.25, pb: 0.5,
                          color: 'rgba(255,255,255,0.42)',
                          fontWeight: 700, fontSize: 11,
                          letterSpacing: '0.06em', textTransform: 'uppercase',
                        }}
                      >
                        {SIDEBAR_GROUP_LABELS[groupKey] || groupKey}
                      </Typography>
                    )}
                    {parents.map((item) => (
                      <Box key={item.path} sx={{ borderBottom: MENU_DIVIDER }}>
                        <NavItem
                          item={item}
                          collapsed={showCollapsed}
                          active={isNavItemActive(item.path, location.pathname)}
                          onNavigate={goTo}
                          enhanced
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
          </>
        )}
      </Box>

      {isSuperAdmin && (
        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.15)', py: 1.25, bgcolor: 'rgba(0,0,0,0.08)' }}>
          {!showCollapsed && (
            <Typography variant="caption" sx={{
              px: 2.5, pb: 0.5, display: 'block', color: 'rgba(255,255,255,0.32)',
              fontWeight: 600, letterSpacing: '0.06em', fontSize: 10, textTransform: 'uppercase',
            }}>
              Configuration
            </Typography>
          )}
          {superAdminFooterNav.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              collapsed={showCollapsed}
              active={isNavItemActive(item.path, location.pathname)}
              onNavigate={goTo}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {panel}
      </Drawer>
    );
  }

  return (
    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
      {panel}
    </Box>
  );
};

export const SIDEBAR_WIDTH = DRAWER_WIDTH;
export const SIDEBAR_COLLAPSED_WIDTH = COLLAPSED_WIDTH;
